import dayjs, { type Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";
import { getPrivateInstance } from "../_privateInstance";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  SeasonApiRecord,
  SeasonsListResponse,
  UpdateSeasonRequest,
  UpdateSeasonResponse,
} from "../../types/gamePass";
import type { EditSeasonValues } from "../../components/GamePass/SeasonDetails/EditSeasonModal/EditSeasonModal";
import type { ScheduleValues } from "../../components/GamePass/CreateSeason/createSeasonData";
import { unwrapGamePassEntity } from "./responseMappers";

dayjs.extend(utc);

const buildUpdateSeasonUrl = (seasonId: string): string =>
  API_ROUTES.UPDATE_SEASON.replace("{season_id}", seasonId);

// Canonicalise a stored datetime to the same `.000Z` serialisation that
// `.toISOString()` produces, so a semantically-identical instant returned by
// the API in a different format (e.g. `+00:00`) compares equal. Unparseable
// values are returned as-is so the diff still treats them as a change.
const normaliseStoredInstant = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  const parsed = dayjs.utc(value);
  return parsed.isValid() ? parsed.toISOString() : value;
};

// The Schedule pickers hand back the admin's locally selected calendar day
// and wall-clock time. Compose them in that local zone first, then convert the
// resulting instant to UTC so the season starts/ends at the intended moment
// regardless of where the admin is. `.utc()` preserves the absolute instant
// (and respects the DST offset for that date), unlike stamping the local clock
// numbers straight onto a UTC value.
export const combineDateAndTime = (
  date?: Dayjs,
  time?: Dayjs,
): string | undefined => {
  if (!date || !time || !date.isValid() || !time.isValid()) return undefined;
  return date
    .hour(time.hour())
    .minute(time.minute())
    .second(0)
    .millisecond(0)
    .utc()
    .toISOString();
};

export const mapEditSeasonValuesToUpdateSeasonRequest = ({
  current,
  next,
}: {
  current: SeasonApiRecord;
  next: EditSeasonValues;
}): UpdateSeasonRequest => {
  const body: UpdateSeasonRequest = {};

  const trimmedName = next.name.trim();
  if (trimmedName !== current.name) {
    body.name = trimmedName;
  }

  // Description is free-text and clearable: diff the trimmed value against the
  // stored one (treating a missing record value as empty) and ship it even
  // when blanked, so clearing the field actually persists.
  const trimmedDescription = (next.description ?? "").trim();
  const currentDescription = (current.description ?? "").trim();
  if (trimmedDescription !== currentDescription) {
    body.description = trimmedDescription;
  }

  const currentFree = current.free_path_enabled ?? true;
  if (next.tiers.free !== currentFree) {
    body.free_path_enabled = next.tiers.free;
  }

  const currentPremium = current.premium_path_enabled ?? true;
  if (next.tiers.premium !== currentPremium) {
    body.premium_path_enabled = next.tiers.premium;
  }

  // Task submission modes (FE-2080). Sportsbook is editable; casino is locked
  // to AUTO in the UI. Fall back to legacy `task_submission_mode` for older
  // records that predate the per-source fields.
  const currentSportsbook =
    current.sportsbook_submission_mode ?? current.task_submission_mode ?? "AUTO";
  const nextSportsbook = next.sportsbookSubmissionMode ?? "AUTO";
  if (nextSportsbook !== currentSportsbook) {
    body.sportsbook_submission_mode = nextSportsbook;
  }

  const currentCasino = current.casino_submission_mode ?? "AUTO";
  // Casino season default is always AUTO in the UI. `nextCasino` is intentionally
  // a constant — when a legacy record stored a non-AUTO value, this PUT corrects
  // it on save rather than leaving stale data on the backend.
  const nextCasino = "AUTO";
  if (nextCasino !== currentCasino) {
    body.casino_submission_mode = nextCasino;
  }

  // Auto-start links the season to its predecessor via `trigger_season_id`
  // (the trigger lives on this successor and points at the active season). The
  // start/end are still shipped below — the Schedule step auto-fills them (start
  // = active end + 1 min, end = admin-chosen), patching over any placeholder
  // window a draft was seeded with (FE-1970).
  if (next.schedule.autoStartAfter) {
    if (
      next.schedule.previousSeasonId &&
      next.schedule.previousSeasonId !== current.trigger_season_id
    ) {
      body.trigger_season_id = next.schedule.previousSeasonId;
    }
  } else if (current.trigger_season_id) {
    // Auto-start was turned off on a season that previously chained off a
    // predecessor — explicitly clear the link (send `null`) so the backend
    // doesn't retain the stale trigger and auto-start it anyway.
    body.trigger_season_id = null;
  }

  // Banner image: a freshly uploaded key, an explicit clear (`null`), or omit
  // when the edit didn't touch the image (mirrors challenge / task `img_key`).
  const currentImgKey = current.img_key ?? null;
  if (next.imgKey !== undefined && next.imgKey !== currentImgKey) {
    body.img_key = next.imgKey;
  }

  // `nextStart`/`nextEnd` are always `.000Z`-serialised (from `.toISOString()`),
  // but the API can return a semantically-identical instant in a different
  // serialisation (e.g. `+00:00`). Normalise the stored value to the same
  // canonical form before comparing so an unchanged schedule does not produce a
  // spurious update.
  const nextStart = combineDateAndTime(
    next.schedule.startDate,
    next.schedule.startTime,
  );
  if (nextStart && nextStart !== normaliseStoredInstant(current.start_date)) {
    body.start_date = nextStart;
  }

  const nextEnd = combineDateAndTime(next.schedule.endDate, next.schedule.endTime);
  if (nextEnd && nextEnd !== normaliseStoredInstant(current.end_date)) {
    body.end_date = nextEnd;
  }

  return body;
};

// Build the update-season payload from the Create Season wizard's Step 3
// (Schedule). The admin's picked calendar day + wall-clock time are combined and
// converted to UTC, matching the Edit Season modal's serialisation.
//
// When auto-start is on (FE-1970), the draft is patched to start one minute
// after the active season ends (the Schedule step auto-fills those values) and
// is linked to that predecessor via `trigger_season_id` — the trigger lives on
// the successor (this new draft) and points back at the active season. The end
// is the admin-chosen value. The active season itself is never modified.
export const mapScheduleValuesToUpdateSeasonRequest = (
  schedule: ScheduleValues,
): UpdateSeasonRequest => {
  const body: UpdateSeasonRequest = {};

  const startDate = combineDateAndTime(schedule.startDate, schedule.startTime);
  if (startDate) {
    body.start_date = startDate;
  }

  const endDate = combineDateAndTime(schedule.endDate, schedule.endTime);
  if (endDate) {
    body.end_date = endDate;
  }

  if (schedule.autoStartAfter && schedule.previousSeasonId) {
    body.trigger_season_id = schedule.previousSeasonId;
  }

  return body;
};

export type UpdateSeasonVariables = {
  seasonId: string;
  body: UpdateSeasonRequest;
};

const putUpdateSeason = async ({
  seasonId,
  body,
}: UpdateSeasonVariables): Promise<SeasonApiRecord> => {
  const response = await getPrivateInstance().put<UpdateSeasonResponse>(
    buildUpdateSeasonUrl(seasonId),
    body,
  );
  return unwrapGamePassEntity(response.data, "season");
};

export const useUpdateSeason = (): UseMutationResult<
  SeasonApiRecord,
  Error,
  UpdateSeasonVariables
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["game-pass", "seasons", "update"],
    mutationFn: putUpdateSeason,
    onSuccess: (season, variables) => {
      queryClient.setQueriesData<SeasonsListResponse>(
        { queryKey: ["game-pass", "seasons", "list"] },
        (current) => {
          if (!current) return current;
          return {
            ...current,
            seasons: current.seasons.map((record) =>
              (record.id ?? record.season_id) === (season.id ?? season.season_id)
                ? { ...record, ...season }
                : record,
            ),
          };
        },
      );
      queryClient.invalidateQueries({
        queryKey: ["game-pass", "seasons", "list"],
      });
      queryClient.invalidateQueries({
        queryKey: ["game-pass", "challenges", "list", variables.seasonId],
      });
    },
  });
};
